import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './style.scss';
import _ from 'lodash';
import DefaultChip from 'components/Utils/DefaultChip';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Icon from 'components/Utils/NewIcon';
import CalendarUtils from 'services/CalendarUtils';
import UnreadBadge from 'components/Common/UnreadBadge';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { ChatMessages } from 'components/Chat/ChatArchive';
import DefaultModal from 'components/Utils/DefaultModal';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import StaffUtils from 'services/StaffUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import CPPTimelineCall from 'components/Patient/CPP/CPPTimeline/CPPTimelineCall';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import removeMd from 'remove-markdown';
import ChatUtils from 'services/ChatUtils';
import MessageContent from 'components/Chat/HelpdeskWindow/MessageContent';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { IsAdmin } from 'careand-redux/selectors';

const DEFAULT_ARRAY = [];

const PatientCommunication = ({ patient }) => {
  const patientId = patient.id;
  const dispatch = useAppDispatch();

  const patientArchives =
    useAppSelector((state) => state.chat.archives[patientId]) || DEFAULT_ARRAY;
  const patientMessages =
    useAppSelector((state) => state.resource.patientHelpdeskMessages[patientId]) || DEFAULT_ARRAY;
  const admins = useAppSelector((state) => state.admins.byId);
  const notes = useAppSelector((state) => state.healthRecords.byId[patientId]?.communication_notes);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const ownId = useAppSelector((state) => state.authentication.user.id);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [calls, setCalls] = useState([]);
  const [numberElements, setNumberElements] = useState(100);
  const [loading, setLoading] = useState(true);
  const [noteModal, setNoteModal] = useState(false);

  const [toggled, setToggled] = useState({
    chats: true,
    emails: true,
    notes: true,
    calls: true,
    notifications: false,
  });

  const debouncedSearch = useCallback(_.debounce(doSearch, 100), []);

  useEffect(() => {
    debouncedSearch(filter);
  }, [filter, debouncedSearch]);

  function doSearch(text = '') {
    setSearch(text.toLowerCase());
  }

  useEffect(() => {
    Promise.all([
      dispatch(api.Chat.getUserArchives({ userId: patientId, role: 'patient' })),
      dispatch(api.Resource.getPatientEmails({ patientId })),
      dispatch(api.Notifications.getUserNotifications({ userId: patientId })).then(
        (notifications) => {
          if (notifications) setNotifications(notifications);
        },
      ),
      dispatch(api.Resource.getPatientCommunicationNotes({ id: patientId })),
      dispatch(api.Resource.getCalls({ patientId })).then((calls) => {
        if (calls) setCalls(calls);
      }),
    ]).then(() => setLoading(false));
  }, [dispatch, patientId]);

  function toggle(key) {
    setToggled((prevState) => {
      return {
        ...prevState,
        [key]: !prevState[key],
      };
    });
  }

  const allElements = useMemo(() => {
    let results = [];

    let isSame = [];
    _.reverse(_.sortBy(notifications, 'date')).forEach((notification) => {
      if (isSame.length === 0) isSame.push(notification);
      else if (notification.content === isSame[isSame.length - 1].content) {
        isSame.push(notification);
      } else {
        results.push({
          type: 'notifications',
          date: isSame[0].date,
          data: [...isSame],
          id: isSame[0].id,
        });
        isSame = [notification];
      }
    });
    if (isSame.length > 0) {
      results.push({
        type: 'notifications',
        date: isSame[0].date,
        data: [...isSame],
        id: isSame[0].id,
      });
    }

    patientMessages.forEach((message) => {
      results.push({
        type: 'emails',
        date: message.date,
        data: message,
        id: message.id,
      });
    });

    notes?.forEach((note) => {
      results.push({
        type: 'notes',
        date: note.date,
        data: note,
        id: note.id,
      });
    });

    calls.forEach((call) => {
      results.push({
        type: 'calls',
        date: call.date,
        data: call,
        id: call.id,
      });
    });

    patientArchives
      .filter((archive) => archive.messages.length > 0)
      .forEach((archive) => {
        results.push({
          type: 'chats',
          date: archive.date_start,
          data: archive,
          id: archive.id,
        });
      });

    return results;
  }, [notifications, patientArchives, patientMessages, notes, calls]);

  const elements = useMemo(() => {
    let result = [...allElements];

    if (!!search) {
      result = result.filter((element) =>
        JSON.stringify(element.data).toLowerCase().includes(search),
      );
    }

    if (!toggled.chats) result = result.filter(({ type }) => type !== 'chats');
    if (!toggled.emails) result = result.filter(({ type }) => type !== 'emails');
    if (!toggled.notifications) result = result.filter(({ type }) => type !== 'notifications');
    if (!toggled.notes) result = result.filter(({ type }) => type !== 'notes');
    if (!toggled.calls) result = result.filter(({ type }) => type !== 'calls');

    return _.reverse(_.sortBy(result, 'date')).slice(0, numberElements);
  }, [allElements, numberElements, toggled, search]);

  const count = useMemo(() => {
    let result = {};
    allElements.forEach(({ type }) => {
      result[type] = (result[type] || 0) + 1;
    });
    return result;
  }, [allElements]);

  let lastDate;

  return (
    <div className="communications">
      <ProgressMessage show={loading} />
      {!loading && (
        <>
          <div className="control-toolbar">
            <DefaultTextField
              label="Search"
              value={filter}
              style={{ width: '300px', marginRight: '20px' }}
              onChange={(event) => setFilter(event.target.value)}
            />
            {Object.keys(toggled).map((key) => {
              let text = key;
              if (key === 'emails') text = 'Helpdesk Emails';
              return (
                <DefaultChip
                  key={key}
                  onClick={() => toggle(key)}
                  element={
                    <span>
                      {text}
                      <UnreadBadge unreadCount={count[key] || 0} />
                    </span>
                  }
                  className={toggled[key] ? 'active' : ''}
                />
              );
            })}
            <Icon
              src="circle-plus"
              fontAwesomeStyle="fas"
              className="blue"
              alt="Add Communication Note"
              showTooltip={true}
              width="30px"
              height="30px"
              style={{ margin: '0 10px' }}
              onClick={() => setNoteModal({})}
            />
          </div>
          <div className="communications-content">
            {elements.map((element) => {
              let date = CalendarUtils.format(element.date);
              if (date !== lastDate) lastDate = date;
              else date = undefined;
              return (
                <>
                  {!!date && <span className="date small-title">{date}</span>}
                  <div className="element" key={element.id}>
                    {element.type === 'notes' && (
                      <CommunicationNoteBadge
                        note={element.data}
                        admins={admins}
                        physicians={physicians}
                        ownId={ownId}
                        onClick={() => setNoteModal(element.data)}
                      />
                    )}
                    {element.type === 'notifications' && (
                      <NotificationBadge notifications={element.data} />
                    )}
                    {element.type === 'calls' && <CallBadge call={element.data} />}
                    {element.type === 'emails' && (
                      <EmailBadge email={element.data} patient={patient} admins={admins} />
                    )}
                    {element.type === 'chats' && (
                      <ChatBadge
                        chat={element.data}
                        patient={patient}
                        description={`${element.data?.attributes?.channel_type || 'Helpdesk'} Chat`}
                      />
                    )}
                  </div>
                </>
              );
            })}
            {!!noteModal && (
              <DefaultModal
                className="fit-modal communication-note-document-modal"
                title="Add Communication Note"
                wrap={true}
                content={
                  <NewCommunicationNote
                    note={noteModal}
                    patient={patient}
                    onAddNote={() => setNoteModal()}
                  />
                }
                isOpen={!!noteModal}
                onRequestClose={() => setNoteModal()}
              />
            )}
          </div>
          <div className="button-wrapper my-4 ml-1">
            <DefaultButton
              name="Load more"
              onClick={() => setNumberElements(numberElements + 100)}
              hide={elements.length < numberElements}
            />
          </div>
        </>
      )}
    </div>
  );
};

const CallBadge = ({ call }) => {
  return (
    <div className="call-badge badge">
      <CPPTimelineCall call={call} />
    </div>
  );
};

export const CommunicationNoteBadge = ({ note, admins, physicians, ownId, onClick }) => {
  let name = StaffUtils.getName(admins[note.staff_id] || physicians[note.staff_id]);
  if (note.staff_id === note.patient_id) name = 'By Patient';
  const editable = note.staff_id === ownId;
  return (
    <div className="note-badge badge">
      <span className="small-title">
        <Icon src="note" alt={`Communication Note (${note.medium})`} showTooltip={true} />
        {name}
      </span>
      <span
        className={'notification-content p-2' + (editable ? ' clickable' : '')}
        onClick={editable ? onClick : undefined}
      >
        {note.note}
      </span>
    </div>
  );
};

const NewCommunicationNote = ({ note, patient, onAddNote }) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState({ ...note, patient_id: patient.id });
  const [loading, setLoading] = useState(false);

  async function onSave() {
    setLoading(true);
    const result = await dispatch(
      api.Resource.addPatientCommunicationNote({ id: patient.id, communicationNote: value }),
      {
        errorTitle: 'Communication Note',
        meta: true,
      },
    );
    if (result.isSuccess) {
      onAddNote();
    }
    setLoading(false);
  }

  return (
    <div className="new-communication-note">
      <DefaultAutoComplete
        style={{ width: '300px', marginTop: 0, marginBottom: '20px' }}
        name="medium"
        label="Medium"
        placeholder="Select option or type anything"
        input={{
          value: value.medium || '',
          onChange: (medium) => setValue({ ...value, medium }),
        }}
        onUpdateInput={(medium) => setValue({ ...value, medium })}
        dataSource={['Phone', 'In person', 'Email']}
      />
      <span className="small-title">Note</span>
      <TextareaAutosize
        cacheMeasurements
        name="text"
        value={value.note}
        onChange={(event) => setValue({ ...value, note: event.target.value })}
        style={{ color: 'black' }}
      />
      <DefaultButton
        name="Save"
        className="blue"
        icon="floppy-disk"
        disabled={!value.note || !value.medium}
        loading={loading}
        onClick={onSave}
      />
    </div>
  );
};

/**
 * Reopens a closed conversation and assigns it to the admin who clicked, so it
 * comes back as one of their chat bubbles rather than landing in the unclaimed
 * queue for someone else to triage.
 */
const ReopenButton = ({ onClick, loading, alt }) => (
  <Icon
    src="envelope-open"
    loading={loading}
    onClick={onClick}
    width="20px"
    height="20px"
    alt={alt}
    showTooltip={true}
    className="reopen-icon"
  />
);

const ChatBadge = ({ chat, patient, description }) => {
  const dispatch = useAppDispatch();
  const reopening = useIsLoading(api.Chat.reopenArchivedChat, (req) => req?.chatId === chat.id);

  // Strictly `=== false`, matching the server guard. A row with is_active UNSET
  // is not an archived room at all — sendOneMessage writes bare transcript rows
  // for one-off outbound notifications (refill notices), and those must never
  // offer a Reopen that would resurrect them as a live patient-facing room.
  const canReopen = chat.is_active === false;

  async function onReopen() {
    await dispatch(api.Chat.reopenArchivedChat({ chatId: chat.id }), {
      errorTitle: 'Reopen Chat',
    });
  }

  return (
    <div className="chat-badge badge">
      <span className="small-title">
        <Icon src="message" alt="Chat" showTooltip={true} />
        {description}
        {canReopen && <ReopenButton onClick={onReopen} loading={reopening} alt="Reopen Chat" />}
      </span>
      <ChatMessages archive={chat} patient={patient} roomName={chat.id} />
    </div>
  );
};

const EmailBadge = ({ email, patient, admins }) => {
  const dispatch = useAppDispatch();
  // This tab renders for physicians too (via the CPP Communication panel), but
  // /resource/helpdesk/{id}/unarchive is admin-scoped — showing the button to a
  // physician would just hand them a 403 toast.
  const isAdmin = useAppSelector(IsAdmin);
  const reopening = useIsLoading(
    api.Resource.unarchiveHelpdeskMessage,
    (req) => req?.id === email.id,
  );

  async function onReopen() {
    await dispatch(api.Resource.unarchiveHelpdeskMessage({ id: email.id }), {
      errorTitle: 'Reopen Helpdesk Thread',
    });
  }

  return (
    <div className="email-badge badge">
      <span className="small-title">
        <Icon src="envelope" alt="Helpdesk Message" showTooltip={true} />
        {email.subject}
        {!!email.archived && isAdmin && (
          <ReopenButton onClick={onReopen} loading={reopening} alt="Reopen Thread" />
        )}
      </span>
      {(email?.messages || []).map((messageContent) => (
        <MessageContent
          key={messageContent.id}
          patient={patient}
          admins={admins}
          message={email}
          messageContent={messageContent}
        />
      ))}
    </div>
  );
};

const NOTIFICATION_KIND = {
  ios: 'iOS',
  android: 'Android',
  sms: 'SMS',
  email: 'Email',
};

const NotificationBadge = ({ notifications }) => {
  const kinds = useMemo(() => {
    return _.uniqBy(notifications, 'kind')
      .map(({ kind }) => NOTIFICATION_KIND[kind] || kind)
      .join(', ');
  }, [notifications]);

  const { title, content, date, attachments } = useMemo(() => {
    let notification = notifications[0];

    let title = notification.title;
    let content = notification.content;
    let date = notification.date;
    let attachments = notification.attachments || [];

    if (notification.kind === 'email') {
      let data = ChatUtils.cleanMessageContent(notification.content);
      data = data.split('[image]').join('[]');
      data = data.split('] (').join('](');
      content = removeMd(data);
    } else title = undefined;

    return { title, content, date, attachments };
  }, [notifications]);

  return (
    <div className="notification-badge badge">
      <span className="top-title small-title">
        <Icon src="bell" alt={`Notification (${kinds})`} showTooltip={true} />
        Notification - {CalendarUtils.format(date, 'h:mm A')}
      </span>
      <div className="notification-content">
        {title ? <span className="small-title">{title}</span> : null}
        <pre>{content.trim()}</pre>
        {attachments.map((name) => (
          <DefaultChip
            text={name}
            style={{ width: 'fit-content', marginTop: '5px' }}
            backgroundColor="#aaafb4"
            textColor="white"
            avatar={<Icon fontAwesome="paperclip" className="white" height="15px" />}
          />
        ))}
      </div>
    </div>
  );
};

PatientCommunication.propTypes = {
  patient: PropTypes.object.isRequired,
};

export default PatientCommunication;
